import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaMapMarkerAlt, FaSave, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../components/Header';
import MapPicker, { LocationData } from '../components/MapPicker';
import OpeningHoursEditor, { hasOpeningHours } from '../components/OpeningHoursEditor';
import StationPhonePreview from '../components/StationPhonePreview';
import StationPhotoField, {
  noPhotoChange,
  PhotoChange,
  savePhotoChange,
  usePhotoPreview,
} from '../components/StationPhotoField';
import { stationService } from '../services/refuelService';
import { FuelStation, StationRequest } from '../types';

const emptyForm: StationRequest = {
  nomePosto: '',
  cnpj: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  telefone: '',
  email: '',
  aberto24h: false,
  horarios: [],
  latitude: undefined,
  longitude: undefined,
};

const toForm = (station: FuelStation): StationRequest => ({
  nomePosto: station.nomePosto || '',
  cnpj: station.cnpj || '',
  logradouro: station.logradouro || '',
  numero: station.numero || '',
  complemento: station.complemento || '',
  bairro: station.bairro || '',
  cidade: station.cidade || '',
  estado: station.estado || '',
  cep: station.cep || '',
  telefone: station.telefone || '',
  email: station.email || '',
  aberto24h: !!station.aberto24h,
  horarios: station.horarios || [],
  latitude: station.latitude,
  longitude: station.longitude,
});

const inputClass =
  'w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453] disabled:opacity-60';
const labelClass = 'block text-[#888] text-sm font-medium mb-2';
const sectionClass = 'p-5 md:p-6 rounded-2xl bg-[#1a2332] border border-white/10';
const sectionTitleClass = 'text-white font-semibold mb-4';

const REQUIRED_FIELDS: { key: keyof StationRequest; label: string }[] = [
  { key: 'nomePosto', label: 'Nome do posto' },
  { key: 'cnpj', label: 'CNPJ' },
  { key: 'cep', label: 'CEP' },
  { key: 'logradouro', label: 'Logradouro' },
  { key: 'numero', label: 'Número' },
  { key: 'bairro', label: 'Bairro' },
  { key: 'cidade', label: 'Cidade' },
  { key: 'estado', label: 'Estado' },
];

/**
 * Cadastro/edição do posto: formulário à esquerda e prévia do app à direita, atualizada enquanto o parceiro digita.
 */
const StationEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [station, setStation] = useState<FuelStation | null>(null);
  const [formData, setFormData] = useState<StationRequest>(emptyForm);
  const [photo, setPhoto] = useState<PhotoChange>(noPhotoChange);
  const [showMap, setShowMap] = useState(false);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const photoUrl = usePhotoPreview(photo, station?.fotoUrl);

  useEffect(() => {
    if (!id) return;
    stationService
      .getStationById(id)
      .then((response) => {
        setStation(response.data);
        setFormData(toForm(response.data));
      })
      .catch((error: any) => {
        toast.error(error.response?.data?.message || 'Posto não encontrado');
        navigate('/postos', { replace: true });
      })
      .finally(() => setIsLoading(false));
  }, [id, navigate]);

  const previewPrices = useMemo(
    () =>
      (station?.combustiveis || [])
        .filter((fuel) => fuel.precoAtual > 0)
        .map((fuel) => ({ tipoCombustivel: fuel.tipoCombustivel, preco: fuel.precoAtual })),
    [station]
  );

  const setField = (key: keyof StationRequest) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData({ ...formData, [key]: e.target.value });

  const handleLocationSelect = (location: LocationData) => {
    setFormData({ ...formData, latitude: location.latitude, longitude: location.longitude });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missing = REQUIRED_FIELDS.filter(({ key }) => !String(formData[key] ?? '').trim());
    if (missing.length > 0) {
      toast.error(`Preencha: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    if ((formData.cnpj || '').replace(/\D/g, '').length !== 14) {
      toast.error('CNPJ inválido');
      return;
    }
    if (!hasOpeningHours(formData.aberto24h, formData.horarios)) {
      toast.error('Informe o horário de funcionamento');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = station
        ? await stationService.updateStation(station.id, formData)
        : await stationService.createStation(formData);
      await savePhotoChange(response.data.id, photo);
      toast.success(
        response.data.reivindicacaoStatus === 'PENDENTE'
          ? 'Cadastro enviado. O posto aparece no app após a aprovação.'
          : 'Posto salvo'
      );
      navigate('/postos');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar posto');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pb-24 lg:pb-0">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Link to="/postos" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-4">
          <FaArrowLeft /> Meus postos
        </Link>
        <h1 className="text-2xl md:text-3xl font-bold text-white">{isEdit ? 'Editar posto' : 'Cadastrar novo posto'}</h1>
        <p className="text-white/60 text-sm mt-1 mb-8">
          {isEdit
            ? 'Veja ao lado como o posto aparece no aplicativo.'
            : 'O posto fica visível no aplicativo depois que nossa equipe aprovar o cadastro.'}
        </p>

        {isLoading ? (
          <div className="flex justify-center py-20 text-[#089453]">
            <FaSpinner className="animate-spin" size={32} />
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px] items-start">
            <form onSubmit={handleSubmit} className="space-y-6 min-w-0">
              <section className={sectionClass}>
                <h2 className={sectionTitleClass}>Foto do posto</h2>
                <StationPhotoField currentUrl={station?.fotoUrl} value={photo} onChange={setPhoto} />
              </section>

              <section className={sectionClass}>
                <h2 className={sectionTitleClass}>Dados do posto</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className={labelClass}>Nome do posto *</label>
                    <input className={inputClass} value={formData.nomePosto} onChange={setField('nomePosto')} placeholder="Ex: Posto Estrela" />
                  </div>
                  <div>
                    <label className={labelClass}>CNPJ *</label>
                    <input
                      className={inputClass}
                      value={formData.cnpj}
                      onChange={setField('cnpj')}
                      placeholder="00.000.000/0000-00"
                      disabled={isEdit}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Telefone</label>
                    <input className={inputClass} value={formData.telefone} onChange={setField('telefone')} placeholder="(00) 00000-0000" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>E-mail</label>
                    <input type="email" className={inputClass} value={formData.email} onChange={setField('email')} placeholder="contato@posto.com" />
                  </div>
                </div>
              </section>

              <section className={sectionClass}>
                <h2 className={sectionTitleClass}>Endereço</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>CEP *</label>
                    <input className={inputClass} value={formData.cep} onChange={setField('cep')} placeholder="00000-000" />
                  </div>
                  <div>
                    <label className={labelClass}>Logradouro *</label>
                    <input className={inputClass} value={formData.logradouro} onChange={setField('logradouro')} placeholder="Rua, avenida, rodovia..." />
                  </div>
                  <div>
                    <label className={labelClass}>Número *</label>
                    <input className={inputClass} value={formData.numero} onChange={setField('numero')} placeholder="Ex: 123 ou S/N" />
                  </div>
                  <div>
                    <label className={labelClass}>Complemento</label>
                    <input className={inputClass} value={formData.complemento} onChange={setField('complemento')} placeholder="Ex: KM 42" />
                  </div>
                  <div>
                    <label className={labelClass}>Bairro *</label>
                    <input className={inputClass} value={formData.bairro} onChange={setField('bairro')} />
                  </div>
                  <div>
                    <label className={labelClass}>Cidade *</label>
                    <input className={inputClass} value={formData.cidade} onChange={setField('cidade')} />
                  </div>
                  <div>
                    <label className={labelClass}>Estado (UF) *</label>
                    <input
                      className={inputClass}
                      value={formData.estado}
                      onChange={(e) => setFormData({ ...formData, estado: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="SP"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setShowMap(!showMap)}
                    className="flex items-center gap-2 text-sm text-[#84cc34] hover:text-[#089453]"
                  >
                    <FaMapMarkerAlt />
                    {formData.latitude && formData.longitude
                      ? `Localização marcada (${formData.latitude.toFixed(5)}, ${formData.longitude.toFixed(5)}) — alterar`
                      : 'Marcar localização no mapa (opcional: sem ela usamos o endereço)'}
                  </button>
                  {showMap && (
                    <div className="mt-3 rounded-xl overflow-hidden border border-white/10">
                      <MapPicker
                        initialLocation={
                          formData.latitude && formData.longitude
                            ? { latitude: formData.latitude, longitude: formData.longitude }
                            : undefined
                        }
                        onLocationSelect={handleLocationSelect}
                        height={360}
                      />
                    </div>
                  )}
                </div>
              </section>

              <section className={sectionClass}>
                <h2 className={sectionTitleClass}>Horário de funcionamento *</h2>
                <p className="text-xs text-white/50 mb-3">O aplicativo mostra só os postos abertos no momento da busca.</p>
                <OpeningHoursEditor
                  aberto24h={!!formData.aberto24h}
                  horarios={formData.horarios || []}
                  onChange={(aberto24h, horarios) => setFormData({ ...formData, aberto24h, horarios })}
                />
              </section>

              <div className="flex justify-end gap-3">
                <Link
                  to="/postos"
                  className="px-5 py-3 rounded-xl border border-white/10 text-white/80 hover:border-white/30"
                >
                  Cancelar
                </Link>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-br from-[#089453] to-[#84cc34] text-[#0f1419] font-semibold disabled:opacity-60"
                >
                  {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaSave />}
                  {isEdit ? 'Salvar' : 'Enviar para aprovação'}
                </button>
              </div>
            </form>

            <aside className="lg:sticky lg:top-6">
              <p className="text-center text-sm text-white/60 mb-3">Prévia no aplicativo</p>
              <StationPhonePreview
                nomePosto={formData.nomePosto}
                bandeira={station?.bandeira}
                logradouro={formData.logradouro}
                numero={formData.numero}
                complemento={formData.complemento}
                bairro={formData.bairro}
                cidade={formData.cidade}
                estado={formData.estado}
                photoUrl={photoUrl}
                aberto24h={formData.aberto24h}
                horarios={formData.horarios}
                precos={previewPrices}
              />
              <p className="text-center text-xs text-white/40 mt-3 max-w-[390px] mx-auto">
                Distância ilustrativa. Os preços são editados na tela Meus postos.
              </p>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
};

export default StationEditor;
